/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import { Module, Node, Collapse } from '@msdyn365-commerce-modules/utilities';
import classnames from 'classnames';
import * as React from 'react';
import { IMenuItemData, INavigationMenuViewProps } from '@msdyn365-commerce-modules/navigation-menu/dist/types';
import { INavigationMenuProps as INavigationMenuExtensionProps } from '../definition-extensions/navigation-menu.ext.props.autogenerated';
interface ICollapseOpen {
    [name: number]: boolean;
}
interface INavigationState {
    parentMenu?: number;
    activeMenu?: number;
    mobileViewLabelText?: string;
    isMenuCollapseOpen?: ICollapseOpen;
}

/**
 *
 * NavigationMenuView component
 * @extends {React.PureComponent<INavigationMenuViewProps & INavigationMenuExtensionProps<{}>>}
 */
export class NavigationMenuView extends React.PureComponent<
    INavigationMenuViewProps & INavigationMenuExtensionProps<{}>,
    INavigationState
> {
    private static isBackTrack: boolean = false;
    private _currentLevel: number = 0;
    private menuNode: React.RefObject<HTMLUListElement>;
    constructor(props: INavigationMenuViewProps & INavigationMenuExtensionProps<{}>) {
        super(props);
        this.menuNode = React.createRef();
        this.state = {
            activeMenu: undefined,
            mobileViewLabelText: '',
            parentMenu: undefined
        };
        this._closeSubmenu = this._closeSubmenu.bind(this);
    }

    public get currentLevel(): number {
        return this._currentLevel;
    }
    public set currentLevel(value: number) {
        this._currentLevel = value;
    }
    public componentDidMount(): void {
        document.body && document.body.addEventListener('mousedown', this._handleClickOutside);
    }

    public componentWillUnmount(): void {
        document.body && document.body.removeEventListener('mousedown', this._handleClickOutside, false);
    }

    public render(): JSX.Element | null {
        const { isMobileView, MenuList, MobileBackButton, MobileDescriptionContainer, MobileDescriptionLabel, Navigation } = this.props;

        this.currentLevel = 1;
        return (
            <Module
                {...Navigation}
                className={classnames(Navigation.className, isMobileView && this.state.activeMenu !== undefined ? 'child' : 'parent')}
            >
                <div className='col-12'>
                    <div className='container'>
                        <div className='row ms-nav'>
                            <Node {...MenuList} ref={this.menuNode} tabIndex='-1'>
                                {isMobileView && this.state.activeMenu !== undefined && (
                                    <Node {...MobileDescriptionContainer}>
                                        <Node {...MobileBackButton} onClick={this._handleGoBack()} />
                                        <Node {...MobileDescriptionLabel}>{this.state.mobileViewLabelText}</Node>
                                    </Node>
                                )}
                                {this._renderDisplay()}
                            </Node>
                        </div>
                    </div>
                </div>
            </Module>
        );
    }

    private handleCollapse = (collapseNumber: number, collapseItem: boolean) => () => {
        this.setState({
            isMenuCollapseOpen: {
                ...this.state.isMenuCollapseOpen,
                [collapseNumber]: !collapseItem
            }
        });
    };

    private _renderDisplay = (): JSX.Element[] => {
        const { ListItem, menuItemData, isMobileView } = this.props;
        const { activeMenu } = this.state;
        const menuItemList: JSX.Element[] = [];

        if (isMobileView && activeMenu !== undefined && menuItemData.length > 0) {
            let menuItem: IMenuItemData = {};
            for (let i = 0; i < menuItemData.length; i++) {
                if (menuItemData[i] && menuItemData[i].id === activeMenu) {
                    menuItem = menuItemData[i];
                    !NavigationMenuView.isBackTrack
                        ? this.setState({ parentMenu: undefined })
                        : this.setState({
                              parentMenu: undefined,
                              mobileViewLabelText: menuItemData[i].linkText
                          });
                    break;
                }
                menuItem = this._getFromSubMenu(menuItemData[i]) as IMenuItemData;
                if (menuItem && menuItem.id === activeMenu) {
                    break;
                }
            }

            menuItem &&
                menuItemList.push(
                    <Node key={menuItem.id} {...ListItem}>
                        {' '}
                        {this._createMenuItemList(menuItem)}{' '}
                    </Node>
                );
        } else {
            menuItemData.forEach((item: IMenuItemData, index: number) => {
                menuItemList.push(
                    <Node key={index} {...ListItem}>
                        {this._createMenuItemList(item)}
                    </Node>
                );
            });
        }

        return menuItemList;
    };

    private _getFromSubMenu = (item?: IMenuItemData): IMenuItemData | null => {
        const subMenus = item && item.subMenu;
        if (subMenus && subMenus.length > 0) {
            for (let i = 0; i <= subMenus.length - 1; i++) {
                if (subMenus[i].id === this.state.activeMenu) {
                    !NavigationMenuView.isBackTrack
                        ? this.setState({ parentMenu: item && item.id })
                        : this.setState({
                              parentMenu: item && item.id,
                              mobileViewLabelText: subMenus[i].linkText
                          });
                    return subMenus[i];
                }
                const found = this._getFromSubMenu(subMenus[i]);
                if (found) {
                    return found;
                }
            }
        }
        return null;
    };

    private renderMenuItemMobileView = (menuItemData: IMenuItemData) => {
        const menuId = menuItemData.id ? menuItemData.id : 0;
        const isCollapseOpen =
            this.state.isMenuCollapseOpen && this.state.isMenuCollapseOpen[menuId] ? this.state.isMenuCollapseOpen[menuId] : false;
        return (
            <React.Fragment>
                {this._renderButtonMenuItem(menuItemData)}
                <Collapse isOpen={isCollapseOpen} timeout={30}>
                    {this._renderSubMenu(menuItemData.subMenu, menuItemData.id)}
                </Collapse>
            </React.Fragment>
        );
    };

    private renderSubMenuForUniformBySchoolCategory = (menuItemData: IMenuItemData) => {
        const filterNavlist = [];
        if (menuItemData.subMenu) {
            const menuLength = menuItemData.subMenu.length;
            const leftSideNavList = menuLength && menuItemData.subMenu?.slice(1, menuLength / 2 + 1);
            const rightSideNavList = menuLength && menuItemData.subMenu?.slice(menuLength / 2 + 1, menuLength);
            for (let i = 0; i < menuLength / 2 + 1; i++) {
                if (leftSideNavList && leftSideNavList.length > 0) {
                    filterNavlist.push(leftSideNavList.shift());
                }
                if (rightSideNavList && rightSideNavList.length > 0) {
                    filterNavlist.push(rightSideNavList.shift());
                }
            }
            return filterNavlist as IMenuItemData[];
        }
        return;
    };

    private _createMenuItemList(menuItemData: IMenuItemData): JSX.Element | null {
        const {
            config: { uniformBySchoolLink }
        } = this.props;
        const isUniformBySchool = menuItemData.linkText === 'Uniform by School' ? true : false;
        let subMenu = menuItemData.subMenu;
        if (isUniformBySchool) {
            menuItemData.linkURL = uniformBySchoolLink && uniformBySchoolLink.linkUrl.destinationUrl;
            if (!this.props.isMobileView) {
                subMenu = this.renderSubMenuForUniformBySchoolCategory(menuItemData);
            }
        }
        if (subMenu && subMenu.length > 0) {
            if (this.props.isMobileView && this.state.activeMenu !== undefined) {
                return this._renderSubMenu(subMenu, menuItemData.id);
            } else {
                if (this.props.isMobileView) {
                    return this.renderMenuItemMobileView(menuItemData);
                } else {
                    return (
                        <React.Fragment>
                            {isUniformBySchool
                                ? this._renderLinkMenuItem(menuItemData, undefined, true)
                                : this._renderButtonMenuItem(menuItemData)}
                            {this._renderSubMenu(subMenu, menuItemData.id)}
                        </React.Fragment>
                    );
                }
            }
        } else if (menuItemData && menuItemData.linkText && menuItemData.linkURL && menuItemData.linkURL.length > 0) {
            return this._renderLinkMenuItem(menuItemData);
        } else if (menuItemData && menuItemData.linkText && !menuItemData.linkURL) {
            return this._renderSpanMenuItem(menuItemData);
        }

        return null;
    }

    private _renderSubMenu = (subMenusList?: IMenuItemData[], activeMenu?: number): JSX.Element | null => {
        const { ListItem } = this.props;
        const subMenus =
            subMenusList &&
            subMenusList.filter(subItem => {
                // Hide all from all navigation menu
                return subItem.linkText && subItem.linkText.indexOf('All') === -1;
            });

        if (!subMenus || subMenus.length === 0) {
            return null;
        }

        const levelClassName: string = '';
        const menuOptions =
            subMenus &&
            subMenus.map((option: IMenuItemData, idx: number) => {
                let menuItem: JSX.Element | null;

                menuItem = option.linkURL ? this._renderLinkMenuItem(option, idx) : this._renderSpanMenuItem(option);
                return (
                    <Node {...ListItem} key={option.id}>
                        {menuItem}
                    </Node>
                );
            });

        return this._renderMenu(levelClassName, menuOptions, activeMenu);
    };

    private _renderButtonMenuItem = (option: IMenuItemData, activeMenu?: number, index?: number): JSX.Element | null => {
        const { Button, isMobileView } = this.props;
        if (isMobileView) {
            const collapseNumber = option.id ? option.id : 0;
            const collapseItem =
                this.state.isMenuCollapseOpen && this.state.isMenuCollapseOpen[collapseNumber]
                    ? this.state.isMenuCollapseOpen[collapseNumber]
                    : false;
            return (
                <Node
                    key={index}
                    {...Button}
                    aria-haspopup={true}
                    data-parent={activeMenu}
                    onClick={this.handleCollapse(collapseNumber, collapseItem)}
                    className={collapseItem ? 'ms-nav__list__item__button nav-expand' : 'ms-nav__list__item__button'}
                >
                    {option.linkText}
                    <i className='arrow-down' />
                </Node>
            );
        } else {
            return (
                <Node
                    key={index}
                    {...Button}
                    onMouseEnter={this._handleDropdownToggle(option, activeMenu)}
                    onFocus={this._closeSubmenu}
                    aria-haspopup={true}
                    aria-expanded={this.state.activeMenu && this.state.activeMenu === option.id ? true : false}
                    data-parent={activeMenu}
                >
                    {option.linkText}
                    <i className='arrow-down' />
                </Node>
            );
        }
    };
    private _renderLinkMenuItem(option: IMenuItemData, index?: number, mainMenu?: boolean): JSX.Element | null {
        const { Link, context } = this.props;
        const hasSubs = option.subMenu && option.subMenu.length > 0;
        const mandsLink = option.linkURL ? option.linkURL.includes('www.marksandspencer.com') : '';
        let url = option.linkURL;
        let categoryId;
        const sitePath = context.request.sitePath !== null ? context.request.sitePath : '';
        if (hasSubs && !mainMenu) {
            categoryId = option.linkURL && this._getCategoryIdFromUrl(option.linkURL);

            // changes for sandbox/local
            // url = `${context.request.url.requestUrl.origin}${sitePath}/${this._getDestURL()}${categoryId}`;

            // changes for production
            url = `https://${context.request.canonicalDomain}${sitePath}/${this._getDestURL()}${categoryId}`;
        } else if (!hasSubs && !mandsLink && !mainMenu) {
            // changes for sandbox/local
            // url = `${context.request.url.requestUrl.origin}${option.linkURL}`;

            // changes for production
            url = `https://${context.request.canonicalDomain}${option.linkURL}`;
        }
        return (
            <Node {...Link} key={index} href={url}>
                {option.linkText}
            </Node>
        );
    }

    private _getDestURL(): string {
        return 'category-listing/?categoryId=';
    }

    private _getCategoryIdFromUrl = (url: string): string | null => {
        if (!url) {
            return null;
        }

        const splitPath = url.split('/');
        const categoryPath = splitPath[splitPath.length - 1];
        const categoryPathSplitted = categoryPath.split('?');
        const formattedCatId = categoryPathSplitted[0].substring(0, categoryPathSplitted[0].length - 2);
        if (categoryPathSplitted.length > 1) {
            return `${formattedCatId}&${categoryPathSplitted[1]}`;
        } else {
            return formattedCatId;
        }
    };

    private _renderSpanMenuItem = (option: IMenuItemData, index?: number): JSX.Element | null => {
        const { Span } = this.props;
        return (
            <Node key={index} {...Span}>
                {option.linkText}
            </Node>
        );
    };

    private _renderMenu = (level: string, menuOptions: JSX.Element[], currentItem?: number): JSX.Element | null => {
        const { MenuList } = this.props;
        this.currentLevel = 1;
        return (
            <Node {...MenuList} className={classnames(MenuList.className, level)}>
                {menuOptions}
            </Node>
        );
    };

    private _handleDropdownToggle = (data: IMenuItemData, parentId?: number) => () => {
        if (!this.props.isMobileView) {
            this.setState({
                activeMenu: this.state.activeMenu && this.state.activeMenu === data.id! ? undefined : data.id!,
                parentMenu: parentId
            });
        } else {
            NavigationMenuView.isBackTrack = false;
            this.setState({
                activeMenu: data.id,
                mobileViewLabelText: data.linkText!,
                parentMenu: parentId
            });
        }
        this._resetFocus();
    };

    private _handleGoBack = () => () => {
        NavigationMenuView.isBackTrack = true;
        this.setState({ activeMenu: this.state.parentMenu });

        this._resetFocus();
    };

    private _resetFocus = () => {
        if (this.props.isMobileView) {
            setTimeout(() => {
                this.menuNode && this.menuNode.current && this.menuNode.current.focus();
            }, 0);
        }
    };

    // tslint:disable-next-line:no-any
    private _handleClickOutside = (event: any) => {
        if (this.menuNode.current && !this.menuNode.current.contains(event.target)) {
            this.setState({ activeMenu: undefined, mobileViewLabelText: '' });
        }
    };

    private _closeSubmenu = (): void => {
        if (!this.props.isMobileView) {
            this.setState({ activeMenu: undefined, mobileViewLabelText: '' });
        }
    };
}

export default NavigationMenuView;
